import { useEffect, useState } from "react";
import { X, Pencil } from "lucide-react";
import toast from "react-hot-toast";

import GenericTable from "@/components/ui/GenericTable";
import { getReferralDetails, updateReferral } from "@/services/offers";
import ReferralModal from "./AddReferral";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import Pagination from "../common/Pagination";

export default function ReferralList() {
    const [loading, setLoading] = useState(false);
    const [referrals, setReferrals] = useState<any[]>([]);
    const [editingReferral, setEditingReferral] = useState<any | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const { canCreate, canUpdate } = useScreenPermission("referral");

    const handleToggleStatus = async (item: any) => {
        try {
            const formData = new FormData();

            formData.append(
                "Status",
                String(!item.Status)
            );

            const response = await updateReferral(
                item.id,
                formData
            );

            if (response.status === 200) {
                toast.success("Status updated successfully");

                setReferrals((prev) =>
                    prev.map((x) =>
                        x.id === item.id
                            ? { ...x, Status: !x.Status }
                            : x
                    )
                );
            }
        } catch (error) {
            toast.error("Failed to update status");
        }
    };

    const fetchReferrals = async () => {
        try {
            setLoading(true);

            const response = await getReferralDetails(currentPage);

            const responseData = response.data as any;

            if (response.status === 200) {
                setReferrals(responseData?.results || []);
                setTotalPages(responseData?.total_pages || 1);
                setTotalCount(responseData?.count || 0);
            }
        } catch (error) {
            toast.error("Failed to fetch referrals");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReferrals();
    }, [currentPage]);

    const handleAdd = () => {
        setEditingReferral(null);
        setIsModalOpen(true);
    };

    const handleEdit = (item: any) => {
        setEditingReferral(item);
        setIsModalOpen(true);
    };


    const columns = [
        {
            key: "Image",
            label: "Image",
            render: (value: string) => {
                const imageBaseUrl =
                    import.meta.env.VITE_BASE_URL.replace(
                        "/api",
                        ""
                    );

                const imageUrl = value
                    ? `${imageBaseUrl}${value}`
                    : null;

                return imageUrl ? (
                    <img
                        src={imageUrl}
                        alt="Referral"
                        className="w-16 h-16 rounded-xl object-cover border"
                        onError={(e) => {
                            e.currentTarget.src =
                                "https://via.placeholder.com/100?text=No+Image";
                        }}
                    />
                ) : (
                    <div className="w-16 h-16 rounded-xl border flex items-center justify-center text-xs text-gray-500">
                        No Image
                    </div>
                );
            },
        },
        {
            key: "Amount",
            label: "Amount",
            render: (value: any) => (
                <p className="font-semibold text-gray-800">
                    ₹ {value}
                </p>
            ),
        },
        {
            key: "Status",
            label: "Status",
            render: (_: any, row: any) => (
                <button
                    disabled={!canUpdate}
                    onClick={() => handleToggleStatus(row)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${row.Status
                        ? "bg-green-500"
                        : "bg-red-500"
                        } ${!canUpdate
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${row.Status ? "translate-x-6" : "translate-x-1"
                            }`}
                    />
                </button>
            ),
        },
    ];

    return (
        <>
            <div className="space-y-6">
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={20}
                    onPageChange={(page) => setCurrentPage(page)}
                />
                <GenericTable
                    title="Referrals"
                    buttonName={canCreate ? "Add Referral" : ""}
                    handleAdd={canCreate ? handleAdd : undefined}
                    columns={columns}
                    data={referrals}
                    loading={loading}
                    onEdit={canUpdate ? handleEdit : undefined}
                    emptyMessage="No referrals found"
                />
            </div>

            {isModalOpen && (
                <ReferralModal
                    referral={editingReferral}
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingReferral(null);
                    }}
                    onSuccess={() => {
                        setIsModalOpen(false);
                        fetchReferrals();
                    }}
                />
            )}
        </>
    );
}